import { tr } from '@/lib/i18n-core';
import { useState, type SelectHTMLAttributes } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { get, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateOnly, money } from '@/lib/utils';
import { parseMoneyInput } from '@/components/MoneyInput';
import { statusLabel } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/layout';
import { Alert, Badge, Button, Input, Select } from '@/components/ui/primitives';
import {
  BATCH_SEARCH_LIMIT,
  MAX_COUNT_QTY,
  PRODUCT_SEARCH_LIMIT,
  type ProductHit,
  type StockBatchHit,
  type StockSearchResult,
  type StoreOption,
} from './stock-control-types';

export function storeLabel(store: StoreOption): string {
  return store.branch ? `${store.branch.name} — ${store.name}` : store.name;
}

export function useStoreOptions() {
  return useQuery({
    queryKey: ['pharmacy-stores'],
    queryFn: () => get<StoreOption[]>('/pharmacy/stores'),
    staleTime: 5 * 60_000,
  });
}

export function StoreSelect({
  value,
  onChange,
  stores,
  emptyLabel,
  excludeId,
  ...selectProps
}: {
  value: string;
  onChange: (storeId: string) => void;
  stores: StoreOption[] | undefined;
  emptyLabel: string;
  excludeId?: string;
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange'>) {
  return (
    <Select {...selectProps} value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">{emptyLabel}</option>
      {stores
        ?.filter((store) => store.id !== excludeId)
        .map((store) => (
          <option key={store.id} value={store.id}>
            {storeLabel(store)}
          </option>
        ))}
    </Select>
  );
}

export function invalidateStockControl(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const root = String(query.queryKey[0]);
      return root === 'stock' || root.startsWith('stock-control');
    },
  });
}

export function formatQty(qty: number | null | undefined): string {
  if (qty === null || qty === undefined) return '—';
  return String(Math.round(qty * 1000) / 1000);
}

export function signedQty(qty: number): string {
  return qty > 0 ? `+${formatQty(qty)}` : formatQty(qty);
}

export function signedMoney(minor: number): string {
  return minor > 0 ? `+${money(minor)}` : money(minor);
}

export function parseQty(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || Math.abs(value) > MAX_COUNT_QTY) return null;
  return value;
}

export function parseMinor(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const value = parseMoneyInput(trimmed);
  if (value === null || value < 0) return null;
  return value;
}

export function productTitle(product: { genericName: string; brandName?: string | null; strength: string | null }): string {
  return `${product.brandName ?? product.genericName} ${product.strength ?? ''}`.trim();
}

const BADGE_TONES: Record<string, 'success' | 'warning' | 'destructive' | 'muted'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'destructive',
  cancelled: 'muted',
  open: 'warning',
  closed: 'success',
  counted: 'success',
  disputed: 'destructive',
  active: 'success',
  quarantined: 'warning',
  recalled: 'destructive',
};

export function StockStatusBadge({ status }: { status: string }) {
  return <Badge variant={BADGE_TONES[status] ?? 'secondary'}>{statusLabel(status, 'stock_control_status')}</Badge>;
}

export function Pager({
  offset,
  pageSize,
  rowCount,
  hasNext,
  disabled,
  onOffsetChange,
}: {
  offset: number;
  pageSize: number;
  rowCount: number;
  hasNext?: boolean;
  disabled?: boolean;
  onOffsetChange: (offset: number) => void;
}) {
  const canGoNext = hasNext ?? rowCount >= pageSize;
  if (offset === 0 && !canGoNext) return null;
  return (
    <div className="mt-3 flex items-center justify-between gap-2 text-sm text-muted-foreground">
      <span>{rowCount > 0 ? tr('pharmacy', 'rows_x_x', { value: offset + 1, value2: offset + rowCount }) : tr('pharmacy', 'no_rows_on_this_page')}</span>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || offset === 0}
          onClick={() => onOffsetChange(Math.max(0, offset - pageSize))}
        >
          {tr('pharmacy', 'previous')}
        </Button>
        <Button variant="outline" size="sm" disabled={disabled || !canGoNext} onClick={() => onOffsetChange(offset + pageSize)}>
          {tr('pharmacy', 'next')}
        </Button>
      </div>
    </div>
  );
}

export function BatchSearch({
  storeId,
  status,
  disabled,
  disabledHint,
  isPicked,
  pickLabel = 'Add',
  onPick,
}: {
  storeId?: string;
  status?: string;
  disabled?: boolean;
  disabledHint?: string;
  isPicked?: (batch: StockBatchHit) => boolean;
  pickLabel?: string;
  onPick: (batch: StockBatchHit) => void;
}) {
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState<{ q: string; storeId: string } | null>(null);
  const scopeStoreId = storeId ?? '';
  const active = submitted !== null && submitted.storeId === scopeStoreId ? submitted : null;

  const results = useQuery({
    queryKey: ['stock-control-batch-search', scopeStoreId, status ?? '', active?.q ?? ''],
    queryFn: () =>
      get<StockSearchResult>(
        `/pharmacy/stock${qs({ storeId: scopeStoreId || undefined, status, q: active?.q || undefined, limit: BATCH_SEARCH_LIMIT })}`,
      ),
    enabled: active !== null && !disabled,
  });

  const runSearch = () => {
    if (disabled) return;
    setSubmitted({ q: text.trim(), storeId: scopeStoreId });
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={text}
          disabled={disabled}
          placeholder={tr('pharmacy', 'search_by_product_name_code_barcode_or_batch')}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            runSearch();
          }}
        />
        <Button variant="secondary" onClick={runSearch} disabled={disabled} loading={results.isFetching}>
          {tr('pharmacy', 'search')}
        </Button>
      </div>
      {disabled && disabledHint && <p className="text-xs text-muted-foreground">{disabledHint}</p>}
      {active !== null && !disabled && (
        <>
          <div className="max-h-72 overflow-y-auto">
            <DataTable
              dense
              rows={results.data?.data}
              loading={results.isLoading} refreshing={results.isFetching}
              error={results.error}
              rowKey={(row) => row.id}
              onRowClick={(row) => {
                if (!isPicked?.(row)) onPick(row);
              }}
              empty={tr('pharmacy', 'no_batches_with_stock_match_that_search')}
              columns={[
                {
                  header: tr('pharmacy', 'item'),
                  cell: (row) => (
                    <div>
                      <p className="font-medium">{productTitle(row.product)}</p>
                      <p className="text-xs text-muted-foreground">{row.product.code}</p>
                    </div>
                  ),
                },
                ...(scopeStoreId ? [] : [{ header: tr('pharmacy', 'store'), cell: (row: StockBatchHit) => row.store.name }]),
                { header: tr('pharmacy', 'batch'), cell: (row) => <span className="font-mono text-xs">{row.batchNo}</span> },
                { header: tr('pharmacy', 'expires'), cell: (row) => dateOnly(row.expiryDate) },
                { header: tr('pharmacy', 'on_hand'), align: 'right', cell: (row) => `${formatQty(row.qtyOnHand)} ${row.product.unit}` },
                { header: tr('pharmacy', 'cost'), align: 'right', cell: (row) => money(row.purchasePriceMinor) },
                { header: tr('pharmacy', 'sale'), align: 'right', cell: (row) => money(row.salePriceMinor) },
                {
                  header: tr('pharmacy', 'status'),
                  cell: (row) => (row.status === 'active' ? null : <StockStatusBadge status={row.status} />),
                },
                {
                  header: '',
                  align: 'right',
                  cell: (row) => (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isPicked?.(row) ?? false}
                      onClick={(event) => {
                        event.stopPropagation();
                        onPick(row);
                      }}
                    >
                      {isPicked?.(row) ? tr('pharmacy', 'added') : pickLabel}
                    </Button>
                  ),
                },
              ]}
            />
          </div>
          {results.data?.nextCursor && (
            <p className="text-xs text-muted-foreground">
              {tr('pharmacy', 'more_batches_match_than_are_listed_narrow_the')}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export function ProductSearch({
  id,
  selected,
  onSelect,
}: {
  id?: string;
  selected: ProductHit | null;
  onSelect: (product: ProductHit | null) => void;
}) {
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState<string | null>(null);

  const results = useQuery({
    queryKey: ['stock-control-product-search', submitted ?? ''],
    queryFn: () => get<ProductHit[]>(`/pharmacy/products${qs({ q: submitted || undefined, limit: PRODUCT_SEARCH_LIMIT })}`),
    enabled: submitted !== null && selected === null,
  });

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
        <span>
          <span className="font-medium">{productTitle(selected)}</span>
          <span className="ms-2 font-mono text-xs text-muted-foreground">{selected.code}</span>
        </span>
        <Button variant="ghost" size="sm" onClick={() => onSelect(null)}>
          {tr('pharmacy', 'change')}
        </Button>
      </div>
    );
  }

  const runSearch = () => setSubmitted(text.trim());

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          id={id}
          value={text}
          placeholder={tr('pharmacy', 'search_products_by_name_brand_or_code_then')}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            runSearch();
          }}
        />
        <Button variant="secondary" onClick={runSearch} loading={results.isFetching}>
          {tr('pharmacy', 'search')}
        </Button>
      </div>
      {submitted !== null && results.error && <Alert variant="destructive">{describeError(results.error)}</Alert>}
      {submitted !== null && !results.error && !results.isLoading && (results.data?.length ?? 0) === 0 && (
        <p className="text-sm text-muted-foreground">{tr('pharmacy', 'no_products_match_that_search')}</p>
      )}
      {(results.data?.length ?? 0) > 0 && (
        <ul className="max-h-56 divide-y overflow-y-auto rounded-md border text-sm">
          {results.data?.map((product) => (
            <li key={product.id}>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-start hover:bg-accent/50 focus-ring"
                onClick={() => onSelect(product)}
              >
                <span className="font-medium">{productTitle(product)}</span>
                <span className="font-mono text-xs text-muted-foreground">{product.code}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
